import type { AppDispatch } from "../../../common/state/store";
import { getConnection } from "./adbConnection";
import { shellCmd } from "./adbShell";
import { shouldEncrypt, encryptMedia } from "./mediaEncryption";
import { updateMediaPushProgress } from "../state/deviceInstallerSlice";
import { getMediaBasePath, MEDIA_METADATA_FILES } from "../constants";
import { downloadWithProgress, pushFileToDevice, isAbortError } from "./adbHelpers";

export interface MediaManifestItem {
  id: number;
  type: string;
  s3Url: string | null;
  s3UrlPreset2: string | null;
  s3UrlPreset4: string | null;
  updatedAt: string | null;
  sizeInBytes: number;
  sizeInBytesPreset2: number | null;
  sizeInBytesPreset4: number | null;
  name: string | null;
}

/** Extract original filename from S3 URL (matches Unity's url.Split("/").Last().Split("?")[0]) */
function extractFilename(url: string): string {
  const withoutQuery = url.split("?")[0];
  return withoutQuery.split("/").pop() || "unknown";
}

/** Push a small text file to the device */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function pushTextFile(adb: any, path: string, content: string): Promise<void> {
  await pushFileToDevice(adb, path, new TextEncoder().encode(content));
}

/** Read a small text file from the device, returns null if not found */
async function readTextFile(adb: any, path: string): Promise<string | null> {
  try {
    const output = await shellCmd(adb, ["cat", path]);
    // "No such file" or similar error from cat
    if (output.includes("No such file")) return null;
    return output.trim();
  } catch (err) {
    console.warn(`[ADB] readTextFile(${path}) failed:`, err);
    return null;
  }
}

/** Delete all pre-loaded media from the device for the given package */
export async function clearAllMediaFromDevice(packageName: string): Promise<boolean> {
  const adb = getConnection();
  if (!adb) return false;

  const basePath = getMediaBasePath(packageName);
  try {
    await shellCmd(adb, ["rm", "-rf", basePath]);
    return true;
  } catch (err) {
    console.warn(`[ADB] clearAllMediaFromDevice(${packageName}) failed:`, err);
    return false;
  }
}

export interface MediaPushResult {
  pushed: number;
  failed: number;
  skipped: number;
}

/** Check which medias already exist and are up-to-date on the device */
export async function scanDeviceMediaStatus(
  packageName: string,
  medias: MediaManifestItem[],
): Promise<Map<number, boolean>> {
  const adb = getConnection();
  const result = new Map<number, boolean>(); // mediaId → isUpToDate
  if (!adb) return result;

  const basePath = getMediaBasePath(packageName);

  for (const media of medias) {
    const mediaDir = `${basePath}/${media.id}`;
    const storedTimestamp = await readTextFile(
      adb,
      `${mediaDir}/${MEDIA_METADATA_FILES.updatedTime}`,
    );
    const isUpToDate =
      storedTimestamp !== null && media.updatedAt !== null && storedTimestamp === media.updatedAt;
    result.set(media.id, isUpToDate);
  }

  return result;
}

/**
 * Push all media files to the device's persistentDataPath cache.
 * Processes files sequentially to limit memory usage.
 */
export async function pushMediasToDevice(
  packageName: string,
  medias: MediaManifestItem[],
  dispatch: AppDispatch,
  signal: AbortSignal,
): Promise<MediaPushResult> {
  const adb = getConnection();
  if (!adb) throw new Error("No ADB connection");

  const basePath = getMediaBasePath(packageName);
  const result: MediaPushResult = { pushed: 0, failed: 0, skipped: 0 };

  // Ensure base Medias directory exists
  await shellCmd(adb, ["mkdir", "-p", basePath]);

  for (let i = 0; i < medias.length; i++) {
    if (signal.aborted) throw new DOMException("Cancelled", "AbortError");

    const media = medias[i];

    // Pick the right URL: for videos on Android, prefer preset2 (4K)
    const downloadUrl =
      media.type === "video" && media.s3UrlPreset2 ? media.s3UrlPreset2 : media.s3Url;

    if (!downloadUrl) {
      result.skipped++;
      continue;
    }

    const filename = extractFilename(downloadUrl);
    const mediaDir = `${basePath}/${media.id}`;

    dispatch(
      updateMediaPushProgress({
        pushedFiles: i,
        currentFileName: media.name || filename,
        currentFileProgress: 0,
      }),
    );

    try {
      // Check if media already exists and is up-to-date
      const storedTimestamp = await readTextFile(
        adb,
        `${mediaDir}/${MEDIA_METADATA_FILES.updatedTime}`,
      );
      if (
        storedTimestamp !== null &&
        media.updatedAt !== null &&
        storedTimestamp === media.updatedAt
      ) {
        result.skipped++;
        continue;
      }

      // Create media directory
      await shellCmd(adb, ["mkdir", "-p", mediaDir]);

      // Download the file
      const { data: rawData } = await downloadWithProgress(downloadUrl, signal, (percent) => {
        dispatch(
          updateMediaPushProgress({
            pushedFiles: i,
            currentFileName: media.name || filename,
            currentFileProgress: percent,
          }),
        );
      });

      if (signal.aborted) throw new DOMException("Cancelled", "AbortError");

      // Encrypt if needed (images and PDFs only)
      const needsEncryption = shouldEncrypt(media.type);
      const fileData = needsEncryption ? await encryptMedia(rawData) : rawData;

      // Push the media file
      await pushFileToDevice(adb, `${mediaDir}/${filename}`, fileData);

      // Push metadata files
      await pushTextFile(
        adb,
        `${mediaDir}/${MEDIA_METADATA_FILES.encrypted}`,
        needsEncryption ? "True" : "False",
      );
      await pushTextFile(
        adb,
        `${mediaDir}/${MEDIA_METADATA_FILES.updatedTime}`,
        media.updatedAt || "",
      );
      await pushTextFile(adb, `${mediaDir}/${MEDIA_METADATA_FILES.presets}`, "");

      result.pushed++;
    } catch (err: unknown) {
      if (isAbortError(err)) throw err;
      console.error(`Failed to push media ${media.id} (${filename}):`, err);
      result.failed++;
    }
  }

  // Final progress update
  dispatch(
    updateMediaPushProgress({
      pushedFiles: medias.length,
      currentFileName: "",
      currentFileProgress: 100,
    }),
  );

  return result;
}
